import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import UrgencyTimer from './UrgencyTimer';
import EmbeddableVideo from '../Common/EmbeddableVideo';
import { StockPill } from './StockBadge';
import { useEcommerce } from '../../context/EcommerceContext';
import { useCurrency } from '../../context/CurrencyContext';
import ShareModal from '../Modals/ShareModal';

const {
  FiHeart, FiShoppingBag, FiBell, FiCheck, FiShare2,
  FiShield, FiRefreshCw, FiAward, FiVideo, FiChevronUp,
} = FiIcons;

/* ── Responsive image helpers ──────────────────────────────────────────────── */
const buildSrcSet = (url) => {
  if (!url || !url.includes('unsplash.com')) return undefined;
  const w400 = url.replace(/([?&])w=\d+/, '$1w=400');
  const w800 = url.replace(/([?&])w=\d+/, '$1w=800');
  const w1200 = url.replace(/([?&])w=\d+/, '$1w=1200');
  return `${w400} 400w, ${w800} 800w, ${w1200} 1200w`;
};

/* ── Static trust badges ───────────────────────────────────────────────────── */
const TRUST_BADGES = [
  { icon: FiShield,    label: 'Secure Payment' },
  { icon: FiRefreshCw, label: 'Easy Returns'   },
  { icon: FiAward,     label: 'Verified Seller' },
];

const TrustBadges = () => (
  <div className="flex items-center justify-between gap-1 pt-3 mt-3 border-t border-brand-light">
    {TRUST_BADGES.map(({ icon, label }) => (
      <div key={label} className="flex flex-col items-center gap-1 flex-1">
        <div className="w-7 h-7 rounded-xl bg-brand-light flex items-center justify-center text-accent-gold">
          <SafeIcon icon={icon} className="w-3.5 h-3.5" />
        </div>
        <span className="text-[8px] font-black uppercase tracking-widest text-text-muted text-center leading-tight">
          {label}
        </span>
      </div>
    ))}
  </div>
);

/* ── ProductCard ────────────────────────────────────────────────────────────── */
const ProductCard = memo(({ product }) => {
  const navigate = useNavigate();
  const { addToCart, wishlist, toggleWishlist } = useEcommerce();
  const { formatCurrency } = useCurrency();

  const [isAdded,     setIsAdded]     = useState(false);
  const [showNotify,  setShowNotify]  = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [showVideo,   setShowVideo]   = useState(false);

  const isWishlisted = wishlist.some(item => item.id === product.id);
  const isPreOrder   = product.preOrderDate && new Date(product.preOrderDate) > new Date();
  const isOutOfStock = product.stock === 0;
  const hasVideo     = !!product.videoUrl;

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleWishlist    = (e) => { e.stopPropagation(); toggleWishlist(product); };
  const handleShare       = (e) => { e.stopPropagation(); setIsShareOpen(true); };
  const handleToggleVideo = (e) => { e.stopPropagation(); setShowVideo(v => !v); };
  const handleNotify      = (e) => {
    e.stopPropagation();
    setShowNotify(true);
    setTimeout(() => setShowNotify(false), 3000);
  };

  return (
    <>
      <motion.div
        layout
        onClick={() => navigate(`/product/${product.id}`)}
        className="bg-white rounded-[40px] overflow-hidden border border-brand-dark group hover:border-accent-gold transition-all duration-500 shadow-sm hover:shadow-2xl flex flex-col h-full relative cursor-pointer"
      >
        {/* ── Image / thumbnail ─────────────────────────────────────────── */}
        <div className="h-64 relative overflow-hidden">
          <img
            src={product.image}
            srcSet={buildSrcSet(product.image)}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={product.name}
            loading="lazy"
            decoding="async"
            className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 ${isOutOfStock ? 'grayscale opacity-70' : ''}`}
          />

          {/* Out-of-stock overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-accent-navy/20 backdrop-blur-[1px]" />
          )}

          <AnimatePresence>
            {showNotify && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/80 text-white px-6 py-3 rounded-2xl text-xs font-bold whitespace-nowrap z-20 flex items-center gap-2 shadow-2xl backdrop-blur-sm"
              >
                <SafeIcon icon={FiCheck} className="text-green-400" /> We'll email you!
              </motion.div>
            )}
          </AnimatePresence>

          {/* Status badges — top-left stack */}
          <div className="absolute top-6 left-6 flex flex-col gap-2 z-10">
            {discount > 0 && (
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                {discount}% OFF
              </span>
            )}
            {isPreOrder && (
              <span className="bg-accent-purple text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                Pre-Order
              </span>
            )}
            <StockPill stock={product.stock} />
          </div>

          {/* Action buttons — top-right */}
          <div className="absolute top-6 right-6 flex flex-col gap-2 z-10">
            <button
              onClick={handleWishlist}
              className={`p-3 rounded-2xl shadow-xl transition-all ${isWishlisted ? 'bg-red-500 text-white' : 'bg-white/80 backdrop-blur-md text-accent-navy hover:bg-white'}`}
            >
              <SafeIcon icon={FiHeart} className={isWishlisted ? 'fill-current' : ''} />
            </button>
            <button
              onClick={handleShare}
              className="p-3 rounded-2xl shadow-xl bg-white/80 backdrop-blur-md text-accent-navy hover:bg-white transition-all"
            >
              <SafeIcon icon={FiShare2} />
            </button>
            {hasVideo && (
              <button
                onClick={handleToggleVideo}
                title={showVideo ? 'Hide video' : 'Watch product video'}
                className={`p-3 rounded-2xl shadow-xl backdrop-blur-md transition-all ${showVideo ? 'bg-accent-gold text-white' : 'bg-white/80 text-accent-navy hover:bg-white'}`}
              >
                <SafeIcon icon={FiVideo} />
              </button>
            )}
          </div>

          {product.urgencyEnd && (
            <div className="absolute bottom-4 left-4 z-10">
              <UrgencyTimer endTime={product.urgencyEnd} />
            </div>
          )}
        </div>

        {/* ── Embedded video (collapsible) ───────────────────────────── */}
        <AnimatePresence>
          {hasVideo && showVideo && (
            <motion.div
              key="video-panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="overflow-hidden border-b border-brand-light"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-4 pt-4 pb-2">
                <EmbeddableVideo
                  url={product.videoUrl}
                  title={product.name}
                  aspectRatio="16:9"
                  useThumbnail={true}
                  lightbox={false}
                  compact={true}
                  className="rounded-2xl overflow-hidden"
                />
                <button
                  onClick={handleToggleVideo}
                  className="w-full flex items-center justify-center gap-1 mt-2 py-1 text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors"
                >
                  <SafeIcon icon={FiChevronUp} className="w-3 h-3" /> Hide Video
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Card body ─────────────────────────────────────────────── */}
        <div className="p-8 flex-grow flex flex-col">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-gold mb-1">
                {product.category}
              </p>
              <h3 className="text-xl font-serif text-accent-navy group-hover:text-accent-gold transition-colors leading-tight">
                {product.name}
              </h3>
            </div>
          </div>

          <p className="text-sm text-text-muted font-light leading-relaxed mb-6 line-clamp-2 italic">
            "{product.description}"
          </p>

          {/* ── Price + CTA ─────────────────────────────────────────── */}
          <div className="mt-auto pt-6 border-t border-brand-light flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-serif ${isOutOfStock ? 'text-text-muted' : 'text-accent-navy'}`}>
                  {formatCurrency(product.price)}
                </span>
                {product.originalPrice && (
                  <span className="text-xs text-text-muted line-through">
                    {formatCurrency(product.originalPrice)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {isOutOfStock ? (
                <button
                  onClick={handleNotify}
                  className="btn-secondary px-6 py-3 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                >
                  <SafeIcon icon={FiBell} /> Notify
                </button>
              ) : (
                <button
                  onClick={handleAddToCart}
                  disabled={isAdded}
                  className={`px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center gap-2 shadow-lg ${isAdded ? 'bg-green-500 text-white' : 'bg-[#D2F53E] text-accent-navy hover:bg-[#C0E42C]'}`}
                >
                  {isAdded
                    ? <><SafeIcon icon={FiCheck} /> Added</>
                    : <><SafeIcon icon={FiShoppingBag} /> Buy</>
                  }
                </button>
              )}
            </div>
          </div>

          <TrustBadges />
        </div>
      </motion.div>

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        title={product.name}
        url={`${window.location.origin}/#/product/${product.id}`}
        description={product.description}
      />
    </>
  );
});

export default ProductCard;